import { FC, useEffect, useMemo, useState } from 'react';
// Redux
import { useAppSelector } from 'hooks/useStore';
// Selectors
import { selectResult } from 'store/player/playerSelectors';
// Components
import PublicDevicePlayable from 'components/PublicDevicePlayable';
import AppBar from 'components/AppBar';
import TextCountdown from 'components/TextCountdown';
import PoweredBy from 'components/PoweredBy';
// Hooks
import useNavigateTo from 'hooks/useNavigateTo';
// Utilities
import { buildUserName } from 'utilities/Utilities';
// i18next
import { useTranslation } from 'react-i18next';

const QuizGroupLeaderboardPage:FC = () => {
  const { t } = useTranslation();
  // State
  const result:any = useAppSelector(selectResult);

  const quizGroup = useMemo(() => result.quizGroup, [result]);

  const navigateTo = useNavigateTo();

  const [ startsDate, setStartsDate ] = useState('');
  const [ endsDate, setEndsDate ] = useState('');

  const getWidth = (score:number) => {
    const firstPlace = result.results?.[0]?.score;
    const percentage = (score / firstPlace) * 100;
    return percentage > 100 ? 100 : percentage;
  }

  const getPosition = (place:number) => {
    return place === 1 ? '1st' : place === 2 ? '2nd' : place === 3 ? '3rd' : `${place}th`;
  }

  useEffect(() => {
    if ( result && result.quizGroup && result.quizGroup.quizzes ){
      const { startTime, endTime } = result.quizGroup.quizzes.reduce((acc:any, cur:any) => {
        const start = Number(cur.startsDate);
        const end = Number(cur.endsDate);
        acc['startTime'] = !acc.startTime ? start : start < acc.startTime ? start : acc.startTime;
        acc['endTime'] = !acc.endTime ? end : end > acc.endTime ? end : acc.endTime;
        return acc;
      }, {});
      setStartsDate(startTime);
      setEndsDate(endTime);
    }
    // eslint-disable-next-line
  }, [result]);

  if ( !result || !result.quizGroup ) return null;
  return (
    <div
      className="grow flex flex-col"
      style={{ background: result.quizGroup.backgroundImage ? `url('${result.quizGroup.backgroundImage}') top / cover` : '' }}
    >
      <PublicDevicePlayable />
      <div
        className="p-4 grow flex flex-col overflow-y-auto"
      >
        <AppBar handleBack={() => navigateTo('quiz-group')} />
        <div className="flex justify-center mt-4 mb-8">
          <h2 className="text-primary font-bold text-center text-4xl">{t('pages.quizGroupLeaderboard.title')}</h2>
        </div>
        <TextCountdown startsDate={startsDate} endsDate={endsDate} />
        {result.currentUserResult ? (
          <div className="mb-4">
            <h3 className="text-primary text-xl font-bold mb-2">{t('labels.yourScore')}</h3>
            <div
              className="
                relative overflow-hidden
                px-4 py-1 flex items-center gap-6
                bg-gradient-to-b from-white to-gray-300
                text-lg rounded-lg
              "
              style={{ color: quizGroup.resultsPage.currentPlacingFontColor }}
            >
              <div
                className="absolute top-0 left-0 h-full rounded-lg bg-gradient-to-r from-[#e3722d] to-[#f4d549]"
                style={{
                  width: `${getWidth(result.currentUserResult.score)}%`,
                  background: `linear-gradient(to right, ${quizGroup.resultsPage.currentPlacingGradientStart}, ${quizGroup.resultsPage.currentPlacingGradientEnd})`
                }}
              />
              <p className="w-8 z-10 font-bold">{result.currentUserResult.position}</p>
              <p className="z-10 flex-1 whitespace-nowrap text-ellipsis overflow-hidden">{buildUserName(result.currentUserResult)}</p>
              <p className="z-10">{result.currentUserResult.score}</p>
            </div>
          </div>
        ) : null}
        {result.results && result.results.length ? (
          <div>
            <h3 className="text-primary text-xl font-bold mb-2">{t('labels.topPlayers')}</h3>
            <ul>
              {result.results.map((result:any, index:number) => (
                <li
                  key={`leaderboard-item-${index}`}
                  className={`
                    relative overflow-hidden flex items-center
                    px-4 py-1 bg-gradient-to-b from-white to-gray-300 text-lg rounded-lg mb-2 gap-6
                  `}
                  style={{ color: quizGroup.resultsPage.placingFontColor }}
                >
                  <div
                    className="absolute top-0 left-0 h-full rounded-lg"
                    style={{
                      width: `${getWidth(result.score)}%`,
                      background: `linear-gradient(to right, ${quizGroup.resultsPage.placingGradientStart}, ${quizGroup.resultsPage.placingGradientEnd})`
                    }}
                  />
                  <p className="w-8 z-10 font-bold">{getPosition(index+1)}</p>
                  <p className="z-10 flex-1 whitespace-nowrap text-ellipsis overflow-hidden">{buildUserName(result)}</p>
                  <p className="z-10">{result.score}</p>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <div className="flex-1" />
        <PoweredBy />
      </div>
    </div>
  )
}

export default QuizGroupLeaderboardPage;
